import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BellOff, Loader2 } from "lucide-react";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { unsubscribeFromFinalDate } from "@core/services/FinalDateService";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Lien de retrait de l'e-mail « La date est fixée » (spec S3) :
 * /notifications/date/stop?token=<uuid>. Un clic suffit, sans compte.
 */
export default function FinalDateUnsubscribe() {
  const { t } = useTranslation("polls");
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<"working" | "done" | "invalid">(
    UUID_RE.test(token) ? "working" : "invalid",
  );

  useEffect(() => {
    if (!UUID_RE.test(token)) return;
    let cancelled = false;
    unsubscribeFromFinalDate(token)
      .then((ok) => !cancelled && setState(ok ? "done" : "invalid"))
      .catch(() => !cancelled && setState("invalid"));
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen-safe bg-background text-foreground">
      <ProductHeader productType="date" showAuth={false} />
      <main className="max-w-md mx-auto px-4 pt-28 text-center" data-testid="final-date-unsubscribe">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
          {state === "working" ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
          ) : (
            <BellOff className="h-6 w-6 text-muted-foreground" aria-hidden />
          )}
        </div>
        <h1 className="text-xl font-semibold mb-2">{t("final_date.unsubscribe_title")}</h1>
        <p className="text-muted-foreground" role="status" data-testid={`final-date-unsubscribe-${state}`}>
          {state === "working" && t("final_date.unsubscribe_working")}
          {state === "done" && t("final_date.unsubscribe_done")}
          {state === "invalid" && t("final_date.unsubscribe_invalid")}
        </p>
        <a href="/" className="mt-6 inline-block text-sm text-primary hover:underline">
          {t("final_date.unsubscribe_home")}
        </a>
      </main>
    </div>
  );
}
